import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Card, SectionTitle, Btn, Pill, Divider, InputField, TabBar } from '../components/common/UIComponents';
import { useAuth } from '../context/AuthContext';
import { AccessDenied } from '../components/common/PermissionGate';
import api from '../services/api';

export default function TimeClock() {
  const { B } = useTheme();
  const { user, hasPermission } = useAuth();
  const [tab, setTab] = useState('live');
  const [now, setNow] = useState(new Date());
  const [clockedIn, setClockedIn] = useState(false);
  const [currentEntry, setCurrentEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Data states
  const [staffStatus, setStaffStatus] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [timesheetPage, setTimesheetPage] = useState(1);
  const [timesheetTotalPages, setTimesheetTotalPages] = useState(1);
  const [timesheetTotal, setTimesheetTotal] = useState(0);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [weeklyHours, setWeeklyHours] = useState({ worked: 0, remaining: 0, overtime: 0 });
  const [weekData, setWeekData] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedClockIn, setSelectedClockIn] = useState('');
  const [selectedClockOut, setSelectedClockOut] = useState('');

  const canViewStaff = hasPermission('view', 'staff');
  const canManageStaff = hasPermission('manageStaff');

  // Fetch current clock status
  const fetchClockStatus = async () => {
    try {
      const res = await api.get('/timeclock/status');
      setClockedIn(res.data.clocked_in);
      setCurrentEntry(res.data.entry);
    } catch (err) {
      console.error('Failed to fetch clock status:', err);
    }
  };

  // Fetch staff status for live view
  const fetchStaffStatus = async () => {
    try {
      const res = await api.get('/team', { params: { limit: 50 } });
      setStaffStatus(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch staff status:', err);
    }
  };

  // Fetch timesheets
  const fetchTimesheets = async () => {
    setLoading(true);
    try {
      const params = { page: timesheetPage, limit: 10 };
      if (selectedStaff && canManageStaff) params.user_id = selectedStaff;
      if (selectedDate) params.date = selectedDate;
      
      const res = await api.get('/timeclock', { params });
      setTimesheets(res.data.data || []);
      setTimesheetTotalPages(res.data.totalPages || 1);
      setTimesheetTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch timesheets:', err);
      setError('Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  };

  // Fetch weekly hours - FIXED: Ensure numeric values
  const fetchWeeklyHours = async () => {
    try {
      const res = await api.get('/dashboard/reports/staff-hours', {
        params: { start_date: getWeekStart(), end_date: new Date().toISOString().split('T')[0] }
      });
      const userHours = res.data.data?.find(h => h.name === user?.name) || { total_hours: 0 };
      
      // FIX: Convert to number and provide fallback
      const workedHours = Number(userHours.total_hours) || 0;
      
      setWeeklyHours({
        worked: workedHours,
        remaining: Math.max(0, 40 - workedHours),
        overtime: Math.max(0, workedHours - 40)
      });
    } catch (err) {
      console.error('Failed to fetch weekly hours:', err);
      // Set safe defaults on error
      setWeeklyHours({ worked: 0, remaining: 40, overtime: 0 });
    }
  };

  // Fetch pending approvals
  const fetchPendingApprovals = async () => {
    try {
      const res = await api.get('/timeclock', { params: { status: 'pending', limit: 20 } });
      setPendingApprovals(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch pending approvals:', err);
    }
  };

  const getWeekStart = () => {
    const date = new Date();
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(date.setDate(diff)).toISOString().split('T')[0];
  };

  // Generate week data for chart
  const generateWeekData = useCallback(async () => {
    const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const startDate = getWeekStart();
    const hoursData = [];
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      
      try {
        const res = await api.get('/timeclock', { params: { date: dateStr, limit: 100 } });
        const userEntries = res.data.data?.filter(e => e.staff_name === user?.name) || [];
        const totalHours = userEntries.reduce((sum, e) => sum + (Number(e.hours_worked) || 0), 0);
        hoursData.push({
          day: weekDays[i],
          hours: totalHours,
          today: i === new Date().getDay() - 1
        });
      } catch {
        hoursData.push({ day: weekDays[i], hours: 0, today: i === new Date().getDay() - 1 });
      }
    }
    setWeekData(hoursData);
  }, [user?.name]);

  // Clock In
  const handleClockIn = async () => {
    setError('');
    setSuccess('');
    try {
      await api.post('/timeclock/clock-in', { notes: 'Clocked in via web app' });
      setSuccess('Clocked in successfully');
      fetchClockStatus();
      fetchStaffStatus();
      generateWeekData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to clock in');
    }
  };

  // Clock Out
  const handleClockOut = async () => {
    setError('');
    setSuccess('');
    try {
      await api.put('/timeclock/clock-out', { notes: 'Clocked out via web app' });
      setSuccess('Clocked out successfully');
      fetchClockStatus();
      fetchStaffStatus();
      generateWeekData();
      fetchTimesheets();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to clock out');
    }
  };

  // Approve timesheet
  const handleApprove = async (id) => {
    try {
      await api.put(`/timeclock/${id}`, { status: 'approved' });
      setSuccess('Timesheet approved');
      fetchPendingApprovals();
      fetchTimesheets();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to approve timesheet');
    }
  };

  // Manual time adjustment
  const handleManualAdjustment = async () => {
    if (!selectedStaff || !selectedDate || !selectedClockIn || !selectedClockOut) {
      setError('Please fill all fields');
      return;
    }
    
    try {
      await api.post('/timeclock/manual', {
        user_id: selectedStaff,
        date: selectedDate,
        clock_in: selectedClockIn,
        clock_out: selectedClockOut
      });
      setSuccess('Time adjustment added');
      fetchTimesheets();
      fetchPendingApprovals();
      setSelectedStaff('');
      setSelectedDate(new Date().toISOString().split('T')[0]);
      setSelectedClockIn('');
      setSelectedClockOut('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to add time adjustment');
    }
  };

  useEffect(() => {
    fetchClockStatus();
    fetchStaffStatus();
    fetchWeeklyHours();
    generateWeekData();
    
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (tab === 'timesheet') {
      fetchTimesheets();
    } else if (tab === 'approvals') {
      fetchPendingApprovals();
    }
  }, [tab, timesheetPage, selectedStaff, selectedDate]);

  // Calculate elapsed time for current clock-in
  const getElapsedTime = () => {
    if (!currentEntry?.clock_in) return { hh: '00', mm: '00', ss: '00' };
    const elapsed = Math.floor((now - new Date(currentEntry.clock_in)) / 1000);
    const hh = String(Math.floor(elapsed / 3600)).padStart(2, '0');
    const mm = String(Math.floor((elapsed % 3600) / 60)).padStart(2, '0');
    const ss = String(elapsed % 60).padStart(2, '0');
    return { hh, mm, ss };
  };

  const { hh, mm, ss } = getElapsedTime();
  const onDutyStaff = staffStatus.filter(s => s.isClockedIn);

  // Get staff list for filter
  const staffList = staffStatus.map(s => ({ id: s.id, name: s.name }));

  // Safe number formatter helper
  const formatHours = (value) => {
    const num = Number(value);
    return isNaN(num) ? '0.0' : num.toFixed(1);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 'clamp(18px, 5vw, 24px)', fontWeight: 800, color: B.navy }}>Time Clock & Attendance</div>
          <div style={{ fontSize: 12, color: B.muted }}>EVV-compliant · GPS verified</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn small outline color={B.blue} onClick={() => fetchTimesheets()}>Export Timesheets</Btn>
          {canManageStaff && <Btn small>Run Payroll</Btn>}
        </div>
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div style={{ background: '#fee2e2', color: '#dc2626', padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>❌ {error}</span>
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer' }}>×</button>
        </div>
      )}
      {success && (
        <div style={{ background: '#dcfce7', color: '#16a34a', padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>✅ {success}</span>
          <button onClick={() => setSuccess('')} style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer' }}>×</button>
        </div>
      )}

      <TabBar
        tabs={[['live', '🟢 Live Status'], ['timesheet', '📋 Timesheets'], ['approvals', '✅ Approvals'], ['schedule', '📅 Schedule']]}
        activeTab={tab}
        onTabChange={setTab}
      />

      {/* Live Tab */}
      {tab === 'live' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: 16, marginBottom: 16 }}>
            {/* Clock card */}
            <div style={{
              background: `linear-gradient(135deg,${B.navy},#1e5fa8)`, borderRadius: 16,
              padding: 24, position: 'relative', overflow: 'hidden'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{ width: 38, height: 38, borderRadius: '50%', background: `linear-gradient(135deg,${B.green},${B.teal})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff' }}>
                  {user?.name?.charAt(0) || 'U'}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{user?.name || 'User'}</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)' }}>{user?.role?.replace('_', ' ') || 'Staff'}</div>
                </div>
                <div style={{ marginLeft: 'auto' }}>
                  <Pill c={B.green} bg={`${B.green}30`}>{clockedIn ? 'On Duty' : 'Off Duty'}</Pill>
                </div>
              </div>
              {clockedIn && currentEntry ? (
                <>
                  <div style={{ textAlign: 'center', marginBottom: 20 }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>Time Elapsed</div>
                    <div style={{ fontSize: 44, fontWeight: 800, color: '#fff', letterSpacing: '0.05em' }}>
                      {hh}:{mm}<span style={{ fontSize: 26, opacity: 0.6 }}>:{ss}</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>
                      Clocked in at {new Date(currentEntry.clock_in).toLocaleTimeString()}
                    </div>
                  </div>
                  <button onClick={handleClockOut} style={{
                    width: '100%', padding: '12px', borderRadius: 10, border: 'none', cursor: 'pointer',
                    fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                    background: 'linear-gradient(135deg,#dc2626,#b91c1c)', color: '#fff'
                  }}>⏹ Clock Out</button>
                </>
              ) : (
                <>
                  <div style={{ textAlign: 'center', marginBottom: 20 }}>
                    <div style={{ fontSize: 28, fontWeight: 800, color: '#fff', marginBottom: 8 }}>Not Clocked In</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Click below to start your shift</div>
                  </div>
                  <button onClick={handleClockIn} style={{
                    width: '100%', padding: '12px', borderRadius: 10, border: 'none', cursor: 'pointer',
                    fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                    background: 'linear-gradient(135deg,#16a34a,#15803d)', color: '#fff'
                  }}>▶ Clock In</button>
                </>
              )}
            </div>

            {/* Hours card - FIXED: Safe number formatting */}
            <Card>
              <SectionTitle>This Week's Hours <Pill c={B.blue}>Pay Period Active</Pill></SectionTitle>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <div style={{ flex: 1, background: B.bg, borderRadius: 10, padding: '10px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: 9, color: B.muted, fontWeight: 700, textTransform: 'uppercase' }}>Worked</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: B.navy }}>{formatHours(weeklyHours.worked)}h</div>
                </div>
                <div style={{ flex: 1, background: B.bg, borderRadius: 10, padding: '10px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: 9, color: B.muted, fontWeight: 700, textTransform: 'uppercase' }}>Remaining</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: B.green }}>{formatHours(weeklyHours.remaining)}h</div>
                </div>
                <div style={{ flex: 1, background: B.bg, borderRadius: 10, padding: '10px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: 9, color: B.muted, fontWeight: 700, textTransform: 'uppercase' }}>Overtime</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: weeklyHours.overtime > 0 ? '#dc2626' : B.muted }}>
                    {formatHours(weeklyHours.overtime)}h
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 60, marginBottom: 4 }}>
                {weekData.map((d, i) => {
                  const pct = Math.min(d.hours / 8, 1);
                  return (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <div style={{ fontSize: 8, fontWeight: 700, color: d.today ? B.green : B.muted }}>{d.hours > 0 ? formatHours(d.hours) : ''}</div>
                      <div style={{ width: '100%', borderRadius: 4, height: Math.max(4, pct * 48), background: d.today ? `linear-gradient(180deg,${B.green},${B.teal})` : `${B.blue}30`, border: d.today ? `1.5px solid ${B.green}` : 'none' }} />
                      <div style={{ fontSize: 8, color: d.today ? B.navy : B.muted, fontWeight: d.today ? 700 : 400 }}>{d.day}</div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>

          {/* Live staff status - Only for managers */}
          {canViewStaff && (
            <Card>
              <SectionTitle>Live Staff Status</SectionTitle>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                {staffStatus.map((s, i) => {
                  const isClockedIn = s.status === 'On Duty';
                  const sc = isClockedIn ? B.green : B.muted;
                  return (
                    <div key={i} style={{ border: `1.5px solid ${isClockedIn ? B.green + '40' : B.border}`, borderRadius: 12, padding: 12, background: isClockedIn ? `${B.green}05` : B.white }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: `linear-gradient(135deg,${B.green},${B.teal})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff', flexShrink: 0 }}>{s.initials || s.name?.charAt(0)}</div>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: B.navy }}>{s.name?.split(' ')[0]}</div>
                          <div style={{ fontSize: 9, color: B.muted }}>{s.role}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: sc }} />
                        <span style={{ fontSize: 10, fontWeight: 700, color: sc }}>{isClockedIn ? 'On Duty' : 'Off Duty'}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Timesheet Tab */}
      {tab === 'timesheet' && (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${B.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: B.navy }}>Timesheets</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <InputField
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{ width: 150 }}
              />
              {canManageStaff && (
                <select
                  value={selectedStaff}
                  onChange={(e) => setSelectedStaff(e.target.value)}
                  style={{ padding: '7px 12px', borderRadius: 8, border: `1.5px solid ${B.border}`, fontSize: 12, background: B.white }}
                >
                  <option value="">All Staff</option>
                  {staffList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              )}
              <Btn small outline color={B.blue} onClick={() => { setTimesheetPage(1); fetchTimesheets(); }}>Refresh</Btn>
            </div>
          </div>
          
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: B.muted }}>Loading timesheets...</div>
          ) : timesheets.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: B.muted }}>No timesheet entries found</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: B.bg }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted }}>Staff</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted }}>Date</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted }}>Clock In</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted }}>Clock Out</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted }}>Hours</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timesheets.map((entry, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${B.border}44` }}>
                        <td style={{ padding: '10px 12px', fontSize: 12, fontWeight: 600, color: B.navy }}>{entry.staff_name}</td>
                        <td style={{ padding: '10px 12px', fontSize: 12, color: B.muted }}>{new Date(entry.clock_in).toLocaleDateString()}</td>
                        <td style={{ padding: '10px 12px', fontSize: 12, color: B.green }}>{new Date(entry.clock_in).toLocaleTimeString()}</td>
                        <td style={{ padding: '10px 12px', fontSize: 12, color: B.text }}>{entry.clock_out ? new Date(entry.clock_out).toLocaleTimeString() : '—'}</td>
                        <td style={{ padding: '10px 12px', fontSize: 12, fontWeight: 700, color: B.navy }}>
                          {entry.hours_worked != null ? formatHours(entry.hours_worked) : '—'}h
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <Pill c={entry.clock_out ? B.green : '#f59e0b'}>{entry.clock_out ? 'Completed' : 'In Progress'}</Pill>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {timesheetTotalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: '16px' }}>
                  <Btn small outline disabled={timesheetPage === 1} onClick={() => setTimesheetPage(p => p - 1)}>Previous</Btn>
                  <span style={{ fontSize: 12, padding: '4px 12px', background: B.bg, borderRadius: 20 }}>Page {timesheetPage} of {timesheetTotalPages}</span>
                  <Btn small outline disabled={timesheetPage === timesheetTotalPages} onClick={() => setTimesheetPage(p => p + 1)}>Next</Btn>
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {/* Approvals Tab - Only for managers */}
      {(tab === 'approvals' && canManageStaff) && (
        <Card>
          <SectionTitle>Pending Approvals</SectionTitle>
          {pendingApprovals.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: B.muted }}>No pending approvals</div>
          ) : (
            pendingApprovals.map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '12px 0', borderBottom: i < pendingApprovals.length - 1 ? `1px solid ${B.border}` : 'none' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: B.navy }}>{item.staff_name}</div>
                  <div style={{ fontSize: 11, color: B.muted }}>{new Date(item.clock_in).toLocaleString()} → {item.clock_out ? new Date(item.clock_out).toLocaleTimeString() : 'Still clocked in'}</div>
                </div>
                <div style={{ fontSize: 14, fontWeight: 800, color: B.navy }}>{formatHours(item.hours_worked)}h</div>
                <Btn small style={{ background: B.green, color: '#fff', border: 'none' }} onClick={() => handleApprove(item.id)}>Approve</Btn>
              </div>
            ))
          )}
          
          <Divider />
          <SectionTitle>Manual Time Adjustment</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, alignItems: 'end' }}>
            <select
              value={selectedStaff}
              onChange={(e) => setSelectedStaff(e.target.value)}
              style={{ padding: '7px 12px', borderRadius: 8, border: `1.5px solid ${B.border}`, fontSize: 12, background: B.white }}
            >
              <option value="">Select Staff...</option>
              {staffList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <InputField type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
            <InputField type="time" value={selectedClockIn} onChange={(e) => setSelectedClockIn(e.target.value)} />
            <InputField type="time" value={selectedClockOut} onChange={(e) => setSelectedClockOut(e.target.value)} />
            <Btn onClick={handleManualAdjustment}>Add Adjustment</Btn>
          </div>
        </Card>
      )}

      {/* Schedule Tab - Placeholder */}
      {tab === 'schedule' && (
        <Card>
          <SectionTitle>Weekly Schedule</SectionTitle>
          <div style={{ textAlign: 'center', padding: 60, color: B.muted }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📅</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: B.navy, marginBottom: 8 }}>Schedule Management</div>
            <div style={{ fontSize: 12 }}>Schedule feature coming soon. Check back later.</div>
          </div>
        </Card>
      )}
    </div>
  );
}